/* eslint-disable  react-hooks/exhaustive-deps*/
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import ChartActions from "store/actions/charts";
import { resetLayoutsToEmpty } from "store/actions/layout.js";
import { updateSidePanelAction } from "store/actions/sidepanel";
import {
  addDefaultWidget,
  editChartAlarms,
  fetchChartData,
  fetchDataOnZoom,
  getChartLayouts,
  refreshPlotData,
  updateChartFilters
} from "store/thunk/charts";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";
import useParamsHandler from "hooks/useLayoutHandler.js";

import ChartUtil from "utils/ChartUtil.js";
import getLogger from "utils/logger.util.js";
import { getGraphDataPointsInterval, getLayout } from "utils/utils.js";
import { ChartStrings } from "constants/constantStrings";
import { DATE_TIME_SECONDS_FORMAT, LOCAL_TIME_ZONE, LOCAL_TZ_CODE } from "constants/globalConstants";

import AddBaseLineModal from "components/AddBaseLineModal/index.js";
import BadgeLabel from "components/BadgeLabel";
import ChartAlarms, { CHART_ALARMS_ACTION } from "components/ChartAlarms/ChartAlarms.jsx";
import PageLoading, { OverlayLoader } from "components/PageLoading";
import ScreenStatusBox from "components/ScreenStatusBox/index.js";
import TextWithRightArrow from "components/TextWithRightArrow/index.js";
import WindowWidth from "components/WindowWidth.jsx";

import ChartConstant from "../../constants/chartConstant.js";

import ChartDownloadData from "./ChartDownloadData/index.js";
import ChartHeader from "./ChartHeader/ChartHeader.jsx";
import ChartNormalizedTagDialog from "./ChartHeader/ChartNormalizedTagDialog.jsx";
import { parseChartAlarms } from "./ChartHelper/ChartAlarmsUtil.js";
import { parseChartData, parseZoomData } from "./ChartHelper/ChartDataParser.js";
import ChartNoData from "./ChartNoData/index.js";
import ChartSideNav from "./ChartSideNav/index.js";
import ChartTags from "./ChartTags/index.js";
import PlotlyChart from "./PlotlyChart.jsx";
import "./index.scss";

const ChartView = () => {
  const Logger = getLogger("ChartView");
  const dispatch = useDispatch();
  const { getCurrentLayout } = useParamsHandler();
  const CHART_HEADER_HEIGHT = 175;
  const [height, width] = WindowWidth();
  const { CHART_HEADER_ACTIONS } = ChartConstant;
  const { resetChartTagsData, resetChartData, setChartTagDataLoading, setEditCurrentView } = ChartActions;

  const { layout, sidePanelData, chartData } = useSelector(state => state);
  const {
    IsLoading,
    IsChartUpdating,
    Tags,
    Format,
    TagsToShow,
    PlotData,
    TagsWithoutData,
    PlotZoomData,
    CurrentView,
    DurationRanges,
    ChartXAxisRange,
    Alarms,
    TagsInDb
  } = chartData;
  const { isChartPanelOpened } = sidePanelData;
  const { currentLayouts } = layout;
  const { showShowOverlayLoader } = layout;

  const [isChartDisplayed, setIsChartDisplayed] = useState(false);
  const [isLiveChart, setIsLiveChart] = useState(false);
  const [chartContainerHeight, setChartContainerHeight] = useState(height - CHART_HEADER_HEIGHT);
  const [plotlyData, setPlotlyData] = useState({});
  const [plotData, setPlotData] = useState({});
  const [plotDurationMsg, setPlotDurationMsg] = useState(null);
  const [tagsWithData, setTagsWithData] = useState([]);
  const [currentAlarms, setCurrentAlarms] = useState(Alarms);
  const [currentLayout, setCurrentLayout] = useState(null);
  const [createBaseline, setCreateBaseline] = useState(false);
  const [baselinesList, setbaselinesList] = useState(null);
  const [isBaselineFormOpen, setIsBaselineFormOpen] = useState(false);
  const [chartLayoutSetting, setChartLayoutSetting] = useState(ChartUtil.getDefaultChartLayout());
  const [tagValue, setTagValue] = useState();
  const [normalizedTagConfig, setNormalizedTagConfig] = useState(null);
  const [zoomTagName, setZoomTagName] = useState("");
  const [baselineValuesMessage, setBaselineValuesMessage] = useState("");
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    const sidenavcontainer = document.getElementById("customModal");
    const sideNavOpen = document.querySelector(".sideNavOpenMenuwidth");
    const sideNavContent = document.querySelector(".layout-nav-container");

    if (createBaseline) {
      document.querySelector(".app-header").classList.add("blur");
      document.querySelector(".side-nav-content").classList.add("blur");
      if (sideNavOpen) sideNavOpen.classList.add("blur");
      if (sidenavcontainer) sidenavcontainer.classList.add("blur");
      if (sideNavContent) sideNavContent.classList.add("blur");
    } else {
      document.querySelector(".app-header").classList.remove("blur");
      document.querySelector(".side-nav-content").classList.remove("blur");
      if (sideNavOpen) sideNavOpen.classList.remove("blur");
      if (sidenavcontainer) sidenavcontainer.classList.remove("blur");
      if (sideNavContent) sideNavContent.classList.remove("blur");
    }
  }, [createBaseline]);

  useEffect(() => {
    dispatch(getChartLayouts(getCurrentLayout));
    return () => {
      dispatch(resetLayoutsToEmpty());
      dispatch(resetChartData());
      dispatch(resetChartTagsData());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const activeLayout = getLayout(currentLayouts);
    if (activeLayout && Object.keys(activeLayout).length) {
      if (currentLayout === null || currentLayout !== activeLayout.ID) {
        setCurrentLayout(activeLayout.ID);
        if (activeLayout.isNewlyAdded) {
          dispatch(addDefaultWidget(activeLayout));
        } else {
          dispatch(fetchChartData(activeLayout));
        }
      }
    } else {
      dispatch(resetChartTagsData());
      dispatch(setChartTagDataLoading(false));
    }
  }, [currentLayouts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (TagsToShow?.length > 0) {
      const hasAnalyticsNormalizedTag = TagsToShow.some(
        item =>
          item.TagId === "normalized_permeate_flow" || item.TagId === "normalized_differential_pressure" || item.TagId === "normalized_salt_passage"
      );
      if (hasAnalyticsNormalizedTag) setBaselineValuesMessage(ChartStrings.baselineValuesMessage);
      else setBaselineValuesMessage("");
    }
  }, [TagsToShow]);

  useEffect(() => formatChartData(), [Format, PlotData, Tags, TagsWithoutData, TagsInDb]);

  useEffect(() => {
    if (Alarms.length) {
      formatChartAlarms();
    }
  }, [Alarms]);

  useEffect(() => {
    if (currentAlarms.length && TagsToShow.length) {
      const { alarmsTraces, shapes } = parseChartAlarms(Format, tagsWithData, currentAlarms);
      const { Layout, Data } = plotData;
      const plotlyData = {
        Data: [...Data, ...alarmsTraces],
        Layout: { ...Layout, shapes }
      };
      setPlotlyData(plotlyData);
    }
  }, [currentAlarms]);

  useEffect(() => {
    const { startTime, endTime } = DurationRanges;
    if (startTime && endTime) {
      const startDate = moment(startTime, "X").format(DATE_TIME_SECONDS_FORMAT);
      const endDate = moment(endTime, "X").format(DATE_TIME_SECONDS_FORMAT);
      const message = ChartStrings.chartDurationMsg.replace("[from]", startDate).replace("[to]", `${endDate} ${LOCAL_TZ_CODE}`);
      setPlotDurationMsg(message);
    }
  }, [DurationRanges]);

  const isNormalizedTagOpen = () =>
    CurrentView === CHART_HEADER_ACTIONS.EDIT_NORMALIZE_FILTERS || CurrentView === CHART_HEADER_ACTIONS.ADD_NORMALIZE_TAG;

  const isAddNormalizedTagOpen = () => CurrentView === CHART_HEADER_ACTIONS.ADD_NORMALIZE_TAG;

  const isEditViewOpened = () => {
    const { EDIT_TAGS, EDIT_ALARMS } = CHART_HEADER_ACTIONS;
    return CurrentView === EDIT_TAGS || CurrentView === EDIT_ALARMS;
  };

  const setChartHeight = tagCount => {
    const MULTIPLOT_TAG_ROW_MIN_HEIGHT = 50; // min row height
    const MULTIPLOT_MAX_ROWS_IN_VIEWPORT = 10;
    if (tagCount >= MULTIPLOT_MAX_ROWS_IN_VIEWPORT) {
      const count = tagCount;
      const heightForSingleRow = Math.max(height / MULTIPLOT_MAX_ROWS_IN_VIEWPORT, MULTIPLOT_TAG_ROW_MIN_HEIGHT);
      const chartHeight = Math.max(heightForSingleRow * count, height);
      setChartContainerHeight(chartHeight);
    }
  };

  const formatChartData = zoomData => {
    const tagsCount = Tags?.length;
    const showTagCount = TagsToShow?.length;
    const isValidChartData = tagsCount && tagsCount !== TagsWithoutData.length && showTagCount && PlotData && PlotData.Time;
    setIsChartDisplayed(isValidChartData);
    if (isValidChartData) {
      const TagsWithValidData = TagsToShow?.filter(({ TagId }) => !TagsWithoutData.find(item => item.TagId === TagId));
      setChartContainerHeight(height - CHART_HEADER_HEIGHT);
      const { Time, ...TagsData } = PlotData;
      const tagsData = TagsWithValidData.map(tag => {
        const Data = TagsData[tag.TagId] || [];
        return { ...tag, Data };
      });
      setTagsWithData(tagsData);
      const dragmode = (PlotZoomData?.xRange && "zoom") || false;
      const defaultLayoutSetting = { ...chartLayoutSetting, dragmode };
      setChartLayoutSetting(defaultLayoutSetting);
      if (Format === ChartConstant.CHART_PLOT_FORMATS.MULTI_PLOT) {
        setChartHeight(showTagCount);
      } else if (Format === ChartConstant.CHART_PLOT_FORMATS.MULTI_Y_PLOT) {
        dispatch(updateSidePanelAction({ isChartPanelOpened: false }));
      }
      const tagsforlimits = TagsInDb;
      const { Layout, Data } = parseChartData(
        Format,
        TagsWithValidData,
        PlotData,
        zoomData,
        defaultLayoutSetting,
        ChartXAxisRange,
        PlotZoomData,
        tagsforlimits
      );
      setPlotData({ Layout, Data });
      const { alarmsTraces, shapes } = parseChartAlarms(Format, tagsData, Alarms);
      const plotlyData = {
        Data: [...Data, ...alarmsTraces],
        Layout: { ...Layout, shapes, autosize: true }
      };
      setPlotlyData(plotlyData);
      dispatch(setChartTagDataLoading(false));
    }
  };

  const isMultiYOptionHidden = () => width < 768 && Format === ChartConstant.CHART_PLOT_FORMATS.MULTI_Y_PLOT;

  const filterAlarmsWitninRanges = (DurationRanges, Alarms) => {
    const { startTime, endTime } = DurationRanges;
    let filterdList = [];
    if (startTime && endTime) {
      const startTimeStamp = moment(new Date(startTime)).unix();
      const endTimeStamp = moment(new Date(endTime)).unix();
      filterdList = Alarms.filter(({ TimeStamp }) => {
        //TimeStamp = `${TimeStamp}:00`.replace(/\s/, 'T').slice(0, 19) + '+00:00';
        const unixTime = moment(new Date(TimeStamp)).unix();
        return startTimeStamp <= unixTime && unixTime <= endTimeStamp;
      });
    }
    return filterdList;
  };

  const formatChartAlarms = zoomData => {
    if (PlotData?.Time) {
      let filteredAlarms = [...Alarms];
      if (zoomData) {
        const { startIndex, endIndex } = zoomData;
        const startTime = PlotData.Time[startIndex];
        const endTime = PlotData.Time[endIndex];
        Logger.log("formatChartAlarms - startIndex, endIndex: ", startIndex, endIndex);
        filteredAlarms = filterAlarmsWitninRanges({ startTime, endTime }, Alarms);
      }
      setCurrentAlarms(filteredAlarms);
    }
  };

  const onPlotZoom = event => {
    objAppInsights.trackEvent("plotZoom");
    if (PlotData) {
      let zoomData = null;
      const isReset = event["xaxis.autorange"] || event["yaxis.autorange"];
      if (event?.autosize || event.dragmode === "zoom" || (isReset && !PlotZoomData.xRange)) return;
      if (!isReset) zoomData = parseZoomData(Format, PlotData.Time, event);
      let group = 10;
      if (zoomData && (!PlotZoomData?.group || PlotZoomData.group > 10)) {
        const getTimestamp = index => moment(zoomData.xRange[index]).unix();
        const DurationRanges = { startTime: getTimestamp(0), endTime: getTimestamp(1) };
        group = getGraphDataPointsInterval(DurationRanges);
        if (!isReset) {
          dispatch(fetchDataOnZoom(zoomData, group));
        }
      }
      dispatch(ChartActions.onPlotZoom({ ...zoomData, isReset, zoomTagName, group }));
    }
  };

  const onPlotRefresh = () => {
    objAppInsights.trackEvent("plotRefresh");
    dispatch(refreshPlotData());
  };

  const onDownloadPlot = () => {
    objAppInsights.trackEvent("plotDownload");
    dispatch(ChartActions.setEditCurrentView(CHART_HEADER_ACTIONS.DOWNLOAD_TAGS_DATA));
  };

  const setCurrentEditView = data => {
    Logger.log("setCurrentEditView: ", data);
  };

  const onCloseNormalizedForm = payload => {
    dispatch(updateChartFilters(payload));
    dispatch(ChartActions.setEditCurrentView(CHART_HEADER_ACTIONS.NONE));
  };

  const onCloseBaselineCreate = () => {
    setCreateBaseline(false);
    dispatch(ChartActions.setEditCurrentView(CHART_HEADER_ACTIONS.NONE));
  };

  const onOpenBaselineCreate = () => {
    objAppInsights.trackEvent("baselineCreateInCharts");
    setIsBaselineFormOpen(true);
    onCloseBaselineCreate();
  };

  const onAlarmActions = params => {
    switch (params.action) {
      case CHART_ALARMS_ACTION.SAVE: {
        const { Alarms, UpdatedAlarmsList } = params.data;
        dispatch(editChartAlarms({ Alarms }, UpdatedAlarmsList));
        break;
      }
      case CHART_ALARMS_ACTION.CANCEL: {
        dispatch(setEditCurrentView(CHART_HEADER_ACTIONS.NONE));
        dispatch(ChartActions.updateAlarms({}));
        break;
      }
      default:
        dispatch(ChartActions.updateAlarms({ updatedAlarms: params.data }));
    }
  };

  const onTagHover = tagName => setZoomTagName(tagName);

  return (
    <>
      <div className="sideNav height--100">
        {IsLoading ? (
          <PageLoading />
        ) : (
          <>
            {(IsChartUpdating || showShowOverlayLoader) && !isLiveChart && <OverlayLoader />}
            <div className="sideNavMainContainer height--100">
              <ChartSideNav
                currentEditView={CurrentView}
                setCurrentEditView={setCurrentEditView}
                setTagValue={setTagValue}
                setNormalizedTagConfig={setNormalizedTagConfig}
              />
              <div
                className={`fullWidth ${createBaseline ? `chart-sidenav-container-blur` : `chart-sidenav-container`} ${
                  isNormalizedTagOpen() && !isChartPanelOpened ? "side-menu-opened" : ""
                }`}
              >
                <ChartHeader
                  setIsLiveChart={setIsLiveChart}
                  setCreateBaseline={setCreateBaseline}
                  createBaseline={createBaseline}
                  setChartLayoutSetting={setChartLayoutSetting}
                  chartLayoutSetting={chartLayoutSetting}
                  isAddNormalizedTagOpen={isAddNormalizedTagOpen()}
                  tagValue={tagValue}
                  setTagValue={setTagValue}
                  normalizedTagConfig={normalizedTagConfig}
                  setNormalizedTagConfig={setNormalizedTagConfig}
                />
                <>
                  {isChartDisplayed && plotlyData.Data?.length > 0 ? (
                    <>
                      {!createBaseline && (
                        <div className="chart-header--drop-down--date-label">
                          <BadgeLabel badgeText={plotDurationMsg} />
                        </div>
                      )}
                      <div id="chart-area">
                        {createBaseline && (
                          <ScreenStatusBox
                            type="info"
                            className="baseline-container"
                            message={
                              <span>
                                {ChartStrings.CREATE_BASELINE}
                                <TextWithRightArrow
                                  fontStyle="baseline-manually-text"
                                  text={ChartStrings.ENTER_MANUALLY}
                                  onClick={onOpenBaselineCreate}
                                />
                              </span>
                            }
                            onClose={onCloseBaselineCreate}
                          />
                        )}
                        <div className="flex-center-center" style={{ height: `${chartContainerHeight}px` }}>
                          {isMultiYOptionHidden() ? (
                            <div>{ChartStrings.mobileMultiY}</div>
                          ) : (
                            <>
                              <PlotlyChart
                                plotData={plotlyData.Data}
                                plotLayout={plotlyData.Layout}
                                isLiveChart={isLiveChart}
                                plotEvents={{ onPlotRefresh, onDownloadPlot, onPlotZoom }}
                                createBaseline={createBaseline}
                                setCreateBaseline={setCreateBaseline}
                                baselinesList={baselinesList}
                                setbaselinesList={setbaselinesList}
                                onTagHover={onTagHover}
                              />

                              {isBaselineFormOpen && (
                                <AddBaseLineModal
                                  setIsBaselineFormOpen={setIsBaselineFormOpen}
                                  baselines={baselinesList}
                                  baselineTimezone={LOCAL_TIME_ZONE}
                                />
                              )}
                            </>
                          )}
                        </div>
                        <div className="descriptionText">{baselineValuesMessage}</div>
                      </div>
                      {isEditViewOpened() && <div className="chart-empty-view">&nbsp;</div>}
                    </>
                  ) : (
                    !IsChartUpdating && <ChartNoData activeLayout={getLayout(currentLayouts)} tags={Tags} />
                  )}
                </>
              </div>
            </div>
          </>
        )}
      </div>
      {CurrentView === CHART_HEADER_ACTIONS.EDIT_TAGS && <ChartTags />}
      {CurrentView === CHART_HEADER_ACTIONS.DOWNLOAD_TAGS_DATA && <ChartDownloadData />}
      {CurrentView === CHART_HEADER_ACTIONS.EDIT_ALARMS && <ChartAlarms OrignialAlarms={chartData.OrignialAlarms} onAlarmActions={onAlarmActions} />}
      {CurrentView === CHART_HEADER_ACTIONS.EDIT_NORMALIZE_FILTERS && (
        <ChartNormalizedTagDialog
          isEdit
          handleClose={onCloseNormalizedForm}
          createBaseline={createBaseline}
          setCreateBaseline={setCreateBaseline}
          baselinesList={baselinesList}
          setbaselinesList={setbaselinesList}
          isChartDisplayed={isChartDisplayed}
        />
      )}
    </>
  );
};

export default ChartView;
